import type { AvlItem } from '@/services/approvedVendorList';

import { ApprovedVendorAllocation, VendorEquipmentTypes } from 'types';

export interface Option {
  id?: string;
  value: string;
  label: string;
  description?: string;
  manufacturer?: string;
  category?: string;
  sku?: string;
  isException?: boolean;
}

const allocationDisplayLabels: Record<ApprovedVendorAllocation, string> = {
  [ApprovedVendorAllocation.full]: 'Full Use',
  [ApprovedVendorAllocation.limited]: 'Limited Use',
  [ApprovedVendorAllocation.exception]: 'Exception Basis',
  [ApprovedVendorAllocation.preApproval]: 'Pre-Approval Required',
  [ApprovedVendorAllocation.safeHarbor]: 'Safe Harbor',
  [ApprovedVendorAllocation.deprecated]: 'No New Projects',
};

/* I'm not sure this is implemented correctly. This will include any manufacturer if there is at
 * least one item they have, in any category, that is not domestic content supported.
 */
export const mapNonDcAvlManufacturerOptions = (options: AvlItem[]): Option[] =>
  options
    .reduce((acc, item) => {
      const option = {
        value: item.manufacturer ?? item.name,
        label: item.manufacturer ?? item.name,
      };
      if (
        (!item.domesticContentSupported || item.manufacturer === 'Other') &&
        !acc.some((item) => item.value === option.value)
      ) {
        acc.push(option);
      }
      return acc;
    }, [] as Option[])
    .sort((a: Option, b: Option) => {
      if (a.label === 'Other') return 1;
      if (b.label === 'Other') return -1;
      return a.label.localeCompare(b.label);
    });

export const formatMountingAvlLabel = (item: Pick<AvlItem, 'manufacturer' | 'sku' | 'description'>): string =>
  `${item.manufacturer} - ${item.sku}` + (item.description ? ` - ${item.description}` : '');

export const mapAllAvlManufacturerOptions = (options: AvlItem[]): Option[] =>
  options
    .map((item) => ({
      value: item.manufacturer + item.sku,
      label: formatMountingAvlLabel(item),
      manufacturer: item.manufacturer,
      category: item.category,
      sku: item.sku,
    }))
    .sort((a: Option, b: Option) => a.label.localeCompare(b.label));

export const mapAvlModelNumberOptions = (options: AvlItem[], manufacturer: string): Option[] =>
  options
    .filter((item) => item.manufacturer === manufacturer)
    .reduce((acc, item) => {
      const baseLabel = item.modelNumber ?? item.name;
      const isException = item.allocation !== ApprovedVendorAllocation.full;
      const option = {
        id: item.id,
        value: baseLabel,
        label: isException ? `${baseLabel} (${allocationDisplayLabels[item.allocation]})` : baseLabel,
        isException,
      };
      if (!acc.some((existing) => existing.value === option.value)) {
        acc.push(option);
      }
      return acc;
    }, [] as Option[])
    .sort((a: any, b: any) => {
      // Sort exception items to the end
      if (a.isException && !b.isException) return 1;
      if (!a.isException && b.isException) return -1;
      return a.label.localeCompare(b.label);
    });

// Returns false when either operand is absent so an unselected model or a
// missing price-cap field never matches; case-insensitive and trimmed otherwise.
export const equalsIgnoreCaseTrim = (a?: string | null, b?: string | null): boolean =>
  a != null && b != null && a.toLowerCase().trim() === b.toLowerCase().trim();

export const maybePrependCustomFreeTextOption = (options: Option[], value: string) => {
  if (value && !options.find((option) => option.value === value)) {
    options.unshift({ value, label: `* ${value}` });
  }
  return options;
};

/** V2 has no server-side `avlFilterOption`; this mirrors server-core's `filterDcEligibleEquipment` client-side. */
export const filterDcEligibleAvlItems = (items: AvlItem[], equipmentType: VendorEquipmentTypes): AvlItem[] =>
  items.filter((item) =>
    equipmentType === VendorEquipmentTypes.storage
      ? item.domesticContentSupported || (item.bessDomesticContentPercentage ?? 0) > 0
      : item.domesticContentSupported ||
        (item.domesticContentPercentageMlpe ?? 0) > 0 ||
        (item.domesticContentPercentageString ?? 0) > 0,
  );

export const resolveSelectedOption = (options: Option[], value?: string | null): Option | null =>
  (value && options.find((option) => option.value === value)) || null;
